<script context="module">
	const expectedNomalStatus = ["OK", "CREATED", "UPDATED", "DELETED"];

</script>

<script lang="ts">
	//UI
	import { AutoComplete } from "@hitit/new_ui/src";
	import SetupCollection from "@hitit/hub/src/v7/components/product/StoreItem/SetupCollection.svelte";
	import AreYouSure from "@hitit/hub/src/v7/components/common/AreYouSure.svelte";
	import Collection from "@hitit/hub/src/v7/components/product/Collection.svelte";
	import ContainerLoading from "@hitit/hub/src/v7/components/common/ContainerLoading.svelte";
	import ActionWithCollections from "@hitit/hub/src/v7/components/product/ActionWithCollections.svelte";
	import DetailViewer from "@hitit/hub/src/v7/components/product/StoreItem/DetailViewer.svelte";
	import EditViewer from "@hitit/hub/src/v7/components/product/StoreItem/EditViewer.svelte";
	import MultipleEditViewer from "@hitit/hub/src/v7/components/product/StoreItem/MultipleEditViewer.svelte";

	//UTILS
	import { api, t, notifications } from "@hitit/hub/src/stores";
	import { popup, saveStatus } from "@hitit/hub/src/v7/store";
	import { getKeyInTranslation, scrollTopBy } from "@hitit/hub/src/v7/utils";
	import { GoBackButton } from "@hitit/new_ui/src";
	import { onDestroy } from "svelte";
	import Category from "@hitit/hub/src/v7/class/StoreItem/Category";
	import StoreItem from "@hitit/hub/src/v7/class/StoreItem/StoreItem";
	import Product from "@hitit/hub/src/v7/class/StoreItem/Product";
	import OptionCollection from "@hitit/hub/src/v7/class/StoreItem/OptionCollection";
	import type { CategoryData, StoreItemData } from "@hitit/api";
	export let storeId = "";
	// Variables
	let loading = false;
	let product: Product;
	let optionCollection: OptionCollection;
	let selectedItems: StoreItem[] = [];
	let selectedCategoryId = "";
	let editmode = false;
	let multipleSelectMode = false;
	let multipleSelectEdit = false;
	let setupCategory: Category | null = null;

	//Functions
	const mobileSaveHandler = () => {
		if (window.innerWidth < 1024) {
			selectedItems = [];
		}
	};
	//AutoComplete
	const autoCompleteCallback = (storeItem: StoreItem) => {
		const itemId = storeItem.id;
		selectedCategoryId = storeItem.cateogry.getId() || "";
		setTimeout(() => {
			const item = document.getElementById(`${itemId}`);
			if (item) {
				item.classList.add("found");
				setTimeout(() => {
					item.classList.remove("found");
				}, 2000);
			}
		}, 20);
	};
	const autocompleteHandler = (search: string) => {
		const items = product.findOption(search);
		return Promise.resolve(
			items.map((v) => {
				const name =
					getKeyInTranslation(v.getTranslation(), "name", "ko") || "";
				return {
					name,
					callback: () => autoCompleteCallback(v),
				};
			})
		);
	};

	//ActionWithCollection
	const multipleEditHandler = () => {
		const condition = !multipleSelectEdit && selectedItems.length === 0;
		multipleSelectEdit = condition ? multipleSelectEdit : !multipleSelectEdit;
	};

	const collectionTextClickHandler = () => {
		if (!multipleSelectMode) selectedItems = [];
	};

	const multipleModeChangeHandler = () => {
		const reg = /^\|TEMP_ITEM\|/;

		selectedItems = multipleSelectMode
			? []
			: selectedItems.filter((v) => !reg.test(v.id || ""));
		multipleSelectMode = !multipleSelectMode;
	};
	const setupCategoryHandler = (e: { detail: Category }) =>
		(setupCategory = setupCategory === null ? e.detail : null);
	const newItemEvent = (e: { detail: Category }) => {
		const displaySort = e.detail.getLastDisplaysort();
		const arg = StoreItem.convienceArg(
			displaySort === 0 ? 0 : displaySort + 1,
			`|TEMP_ITEM|${Math.random()}`
		);
		selectedItems = [StoreItem.convinienceConstructor(arg, "ko", e.detail)];
	};

	const onItemClickEvent = (e: { detail: StoreItem }) => {
		const { detail } = e;
		selectedItems = [detail];
	};
	const multipleViewerDeleteHandler = (e: { detail: string }) => {
		const id = e.detail;
		selectedItems = selectedItems.filter((v) => v.id !== id);
	};

	//PopupHandler
	const categoryDeletePopupHandler = (e: { detail: Category }) => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.category.you_want_to_delete_category",
				accept: () => deleteCollection(e.detail),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const singleStoreItemDeleteHanlder = (e: { detail: StoreItem }) => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.category.you_want_to_delete_item",
				accept: () => deleteSingleItem(e.detail),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const multipleDeleteHandler = () => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.category.you_want_to_delete_item",
				accept: () => deleteMultipleItem(selectedItems),
				cancel: () => ($popup = undefined),
			},
		};
	};

	//Create, Update
	const singleStoreItemEditHandler = async (e: { detail: StoreItem }) => {
		try {
			$saveStatus.storeItems = true;
			const editItem = e.detail;
			const original = selectedCategory!
				.getStoreItems()
				.find((v) => v.id === e.detail.id);
			const isNew = editItem.uid === undefined;
			const id = original?.id;
			const collectionId = editItem.cateogry.getId() || "";
			const dto = Object.assign({}, editItem.getStoreItemDTO(), { id });
			const [, uid, storeItemDTO] = await updateSingleItem(dto, collectionId);
			const newItem = new StoreItem(storeItemDTO, editItem.cateogry);
			selectedCategory!.replaceStoreItemByUid(newItem, uid);
			product = product;
			selectedItems = isNew ? [] : [newItem];
			mobileSaveHandler();
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	const addNewCollection = async (e: { detail: string }) => {
		try {
			$saveStatus.storeItems = true;
			const categories = product.getCategories();
			const lastDisplaySort =
				categories.length > 0
					? categories[categories.length - 1].displaySort + 1
					: 0;
			const category = Category.convinienceConstructor(
				e.detail,
				lastDisplaySort
			);
			const [, id, categoryDTO] = await updateCollection(
				category.getCategoryDTO()
			);

			const newCategory = new Category(categoryDTO);
			product.addNewCategory(newCategory);
			selectedItems = [];
			selectedCategoryId = id;
			product = product;
		} catch (error) {
			errorHandler(error as Error);
			//
		} finally {
			$saveStatus.storeItems = false;
		}
	};
	const setCategoryUpdateHandler = async ({
		detail,
	}: {
		detail: CategoryData;
	}) => {
		try {
			$saveStatus.options = true;
			//
			const dto = detail;
			const originId = dto.id || "";
			const [, , categoryDTO] = await updateCollection(dto);
			const newCategory = new Category(categoryDTO);
			product.replaceCategoryById(originId, newCategory);
			product = product;
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.options = false;
		}
	};
	const multipleViewerSaveHandler = async (e: { detail: StoreItem[] }) => {
		try {
			$saveStatus.storeItems = true;
			for (const item of e.detail) {
				const categoryId = item.cateogry.getId() || "";
				const dto = item.getStoreItemDTO();
				const category = item.cateogry;
				const [, uid, storeItemDTO] = await updateSingleItem(dto, categoryId);
				const newStoreItem = new StoreItem(storeItemDTO, category);
				category.replaceStoreItemByUid(newStoreItem, uid);
			}
			product = product;
			selectedItems = selectedItems.map((v) => {
				const originCategory = v.cateogry;
				const findItem = originCategory.findItemByUid(v.uid || "") as StoreItem;
				return findItem;
			});
			mobileSaveHandler();
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};
	//Delete
	const deleteCollection = async (target: Category) => {
		try {
			$popup = undefined;
			$saveStatus.storeItems = true;
			await $api.deleteCategory(target.getId() || "");
			product.deleteCategory(target);
			selectedItems = [];
			product = product;
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	const deleteSingleItem = async (storeItem: StoreItem) => {
		try {
			$popup = undefined;
			$saveStatus.storeItems = true;
			const uid = storeItem.uid || "";

			await deleteStoreItem(uid);
			storeItem.cateogry.deleteItemByUid(uid);
			selectedItems = selectedItems.filter((v) => v.uid === uid);
			product = product;
		} catch (error) {
			errorHandler(error as Error);
			//
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	const deleteMultipleItem = async (storeItems: StoreItem[]) => {
		try {
			$popup = undefined;
			$saveStatus.storeItems = true;
			for (const item of storeItems) {
				const uid = item.uid || "";
				await deleteStoreItem(uid);
				item.cateogry.deleteItemByUid(uid);
			}
			selectedItems = [];
			product = product;
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.storeItems = false;
		}
	};

	//API
	const updateCollection = async (category: CategoryData) => {
		try {
			const setCategoryResponse = await $api.setStoreCategoryData(
				storeId,
				category
			);
			const getCategoryResponse = await $api.getStoreItems(storeId);
			const { uid, id, status } = setCategoryResponse.data;
			const newCategoryData = getCategoryResponse.data.categoryDTOs.find(
				(v) => v.id === id
			);
			const throwErrorCondition =
				newCategoryData === undefined ||
				expectedNomalStatus.find((v) => status === v) === undefined;

			if (throwErrorCondition) throw new Error(status);

			return [uid, id, newCategoryData] as [string, string, CategoryData];
		} catch (e) {
			throw e;
		}
	};
	const deleteStoreItem = async (storeItemUid: string) => {
		try {
			const { status } = (await $api.deleteStoreItem(storeItemUid)).data;
			const throwErrorCondition =
				expectedNomalStatus.find((v) => status === v) === undefined;
			if (throwErrorCondition) throw new Error(status);
			return storeItemUid;
		} catch (e) {
			throw e;
		}
	};

	const updateSingleItem = async (
		itemDTO: StoreItemData,
		categoryId: string
	) => {
		try {
			const { id, uid, status } = await (
				await $api.setStoreItem(storeId, categoryId, itemDTO)
			).data;
			const newItem = Object.assign({}, itemDTO, { id, uid });
			const throwErrorCondition =
				expectedNomalStatus.find((v) => status === v) === undefined;

			if (throwErrorCondition) throw Error(status);

			return [id, uid, newItem] as [string, string, StoreItemData];
			//
		} catch (error) {
			throw error;
		}
	};
	//self
	async function editmodeHandler() {
		editmode = !editmode;
	}

	const errorHandler = async (error: Error) => {
		const message = error.message;
		const errorTranslation = $t(`error.DATA_WAS_BEEN_MODIFIED`);
		const errorMessage = `
			ERROR STATUS : ${message},<br>
			${errorTranslation}
		`;
		notifications.append("ERROR", errorMessage, "", undefined, "ERROR");
		console.log(error);
		await getStoreItem(storeId);
	};

	async function getStoreItem(storeId: string) {
		try {
			loading = true;

			const [categoryResponse, optionResponse] = await Promise.all([
				$api.getStoreItems(storeId),
				$api.getStoreOptionCollection(storeId),
			]);
			const { categoryDTOs } = categoryResponse.data;
			const { optionCollectionDTOs } = optionResponse.data;
			product = new Product(categoryDTOs, categoryResponse.data.revision);
			optionCollection = new OptionCollection(
				optionCollectionDTOs,
				optionResponse.data.revision
			);
			selectedCategoryId = product
				.getNameIdItemLength()
				.reduce((prev, cur, i) => {
					if (i === 0) return cur.id || "";

					return prev;
				}, "");
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	}

	$: selectedCategory = product
		? product.getCategories().find((v) => v.getId() === selectedCategoryId)
		: null;
	$: detailDisplayCondition =
		(!editmode && selectedItems.length === 1) ||
		(editmode && !multipleSelectMode && selectedItems.length === 1) ||
		(editmode &&
			multipleSelectMode &&
			selectedItems.length >= 1 &&
			multipleSelectEdit);
	$: getStoreItem(storeId);
	onDestroy(() => {
		$popup = undefined;
	});

</script>

<div class="container">
	{#if loading}
		<ContainerLoading />
	{/if}
	{#if product}
		<form id="search-item" class:mobileHide={detailDisplayCondition}>
			<AutoComplete
				placeholder={$t('global.product.category.find_item')}
				searchCallback={autocompleteHandler} />
		</form>
		<div class="actions" class:mobileHide={detailDisplayCondition}>
			<ActionWithCollections
				placeholder={$t('global.product.category.category_name')}
				on:EDIT_CHANGE={editmodeHandler}
				on:MULTIPLE_DELETE={multipleDeleteHandler}
				on:MULTIPLE_SELECT_MODE_CHANGE={multipleModeChangeHandler}
				on:MULTIPLE_EDIT={multipleEditHandler}
				on:CLICK_COLLECTION_TEXT={collectionTextClickHandler}
				on:NEW_COLLECTION={addNewCollection}
				bind:editmode
				bind:multipleSelectMode
				bind:selectedCollectionUid={selectedCategoryId}
				editable={false}
				collections={product.getNameIdItemLength()} />
		</div>
		<div class="list-detail-wrapper">
			<div class="list" class:mobileHide={detailDisplayCondition}>
				{#if selectedCategory}
					<Collection
						on:COLLECTION_SETUP_EVENT={setupCategoryHandler}
						on:ITEM_CLICK_EVENT={onItemClickEvent}
						on:ITEM_DELETE_CLICK_EVENT={singleStoreItemDeleteHanlder}
						on:NEW_ITEM_EVENT={newItemEvent}
						bind:selectedItems
						hasSetup={Boolean(setupCategory)}
						collection={selectedCategory}
						items={selectedCategory?.getStoreItems()}
						editmode={editmode}
						multipleSelectMode={multipleSelectMode}>
						<div slot="setup">
							{#if setupCategory}
								<SetupCollection
									on:UPDATE_CATEGORY={setCategoryUpdateHandler}
									on:DELETE_CATEGORY={categoryDeletePopupHandler}
									category={selectedCategory}
									clickOutsideCallBack={() => (setupCategory = null)} />
							{/if}
						</div>
					</Collection>
				{/if}
			</div>
			<div
				class="detail"
				use:scrollTopBy={detailDisplayCondition}
				class:mobileDisplayCondition={detailDisplayCondition}>
				<div class="goBack">
					<GoBackButton
						on:click={() => {
							selectedItems = [];
							multipleSelectEdit = false;
						}} />
				</div>
				{#if !editmode && selectedItems.length === 1}
					<DetailViewer item={selectedItems[0]} />
				{:else if editmode && !multipleSelectMode && selectedItems.length === 1}
					<EditViewer
						on:EDIT_ITEM_DONE={singleStoreItemEditHandler}
						item={selectedItems[0].getClone()}
						optionCollections={optionCollection.categories} />
				{:else if editmode && multipleSelectMode && selectedItems.length >= 1}
					<MultipleEditViewer
						items={selectedItems.map((v) => v.getClone())}
						on:UPDATE_MULTIPLE_ITEMS={multipleViewerSaveHandler}
						on:MULTIPLE_SELECTED_ITEM_DELETE={multipleViewerDeleteHandler} />
				{/if}
			</div>
		</div>
	{/if}
</div>

<style lang="scss">
	@import "../../variables.scss";

	.container {
		display: flex;
		flex-direction: column;
		width: 100%;
		gap: 22px;

		#search-item {
			width: 100%;
			height: 31px;
			color: #46474b;
		}

		.mobileHide {
			display: none;
		}
		.detail {
			display: none;
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			min-height: 100vh;
			max-height: 100vh;
			overflow-y: auto;
			background-color: #f0f0f4;
			padding: 70px 20px 20px 20px;
			box-sizing: border-box;
			&.mobileDisplayCondition {
				display: flex;
				flex-direction: column;
			}
		}

		.goBack {
			width: 30px;
			height: 27px;
			position: absolute;
			top: 23.2px;
			right: 20px;
		}
	}
	@include DesktopSize {
		.container {
			#search-item {
				width: calc(50% - 15.5px);
				font-size: 1.25rem;
				height: 40px;
			}

			.mobileHide {
				display: block;
			}
			.list-detail-wrapper {
				flex-basis: 100%;
				width: 100%;
				display: flex;
				gap: 20px;
				.list {
					flex-basis: calc(100% * (952 / 1594));
					flex-grow: 1;
				}
			}
			.detail {
				padding: 0;
				display: flex;
				flex-direction: column;
				position: static;
				min-height: 0;
				max-height: none;
				flex-basis: calc(100% * (642 / 1594));
				max-width: 642px;
				/* temp debuging point */
			}
			.goBack {
				display: none;
			}
		}
	}

</style>
